import { API_METHODS } from '@compass/client/constants/api-methods';
import { userService } from '@services/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { currentUser } from '@utils/user';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';

import { API_BASE_URL, getApiPath, joinApiUrl } from '@/services/api.config';
import { subscribeToTokenRefresh } from '@/services/auth.events';
import { useStore } from '@/store';
import type {
  HeartbeatWorkerInMessage,
  HeartbeatWorkerOutMessage,
} from '@/workers/heartbeat.worker';

const AVAILABILITY_QUERY_KEY = ['compass', 'users', 'me', 'availability'] as const;

/** Full URL for the heartbeat endpoint (baseURL + path) */
const HEARTBEAT_URL = joinApiUrl(
  API_BASE_URL,
  getApiPath(API_METHODS.UserService.HeartbeatMyAvailability),
);

export interface AvailabilityContextValue {
  available: boolean;
  isLoading: boolean;
  isPending: boolean;
  setAvailable: (value: boolean) => void;
}

const AvailabilityContext = createContext<AvailabilityContextValue | null>(null);

/** Post a typed message to the heartbeat worker. */
function postToWorker(worker: Worker, msg: HeartbeatWorkerInMessage) {
  worker.postMessage(msg);
}

/** Read current auth context from cookies / store. */
function getAuthContext() {
  const user = currentUser();
  return {
    token: user?.access_token ?? '',
    orgId: useStore.getState().selectedOrganizationId ?? undefined,
  };
}

// --- localStorage helpers for persisting availability across tab closes ---

const AVAILABILITY_STORAGE_KEY = 'compass_last_availability';

function getStoredAvailability(): boolean | null {
  try {
    const value = localStorage.getItem(AVAILABILITY_STORAGE_KEY);
    if (value === null) return null;
    return value === 'true';
  } catch {
    return null;
  }
}

function storeAvailability(available: boolean) {
  try {
    localStorage.setItem(AVAILABILITY_STORAGE_KEY, String(available));
  } catch {
    // localStorage may be unavailable
  }
}

function clearStoredAvailability() {
  try {
    localStorage.removeItem(AVAILABILITY_STORAGE_KEY);
  } catch {
    // best-effort
  }
}

/**
 * AvailabilityProvider — app-wide provider that manages user online/offline status.
 *
 * - Fetches availability when the user is authenticated
 * - Auto-sets the user as "available" on login
 * - Manages heartbeat via a Web Worker (not throttled in background tabs)
 * - Stops heartbeat and clears state on logout
 *
 * Must be placed inside QueryClientProvider.
 */
export const AvailabilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useStore(state => state.isAuthenticated);
  const queryClient = useQueryClient();
  const workerRef = useRef<Worker | null>(null);
  const mountedRef = useRef(true);
  const prevAuthRef = useRef(isAuthenticated);
  const wasAuthenticatedOnMountRef = useRef(isAuthenticated);

  // Fetch current availability (only when authenticated)
  const { data, isLoading } = useQuery({
    queryKey: AVAILABILITY_QUERY_KEY,
    queryFn: () => userService.getMyAvailability({}),
    enabled: isAuthenticated,
    staleTime: 30_000,
    gcTime: 60_000,
  });

  const available = data?.available ?? false;

  // --- Worker lifecycle helpers ---

  const terminateWorker = useCallback(() => {
    if (workerRef.current) {
      postToWorker(workerRef.current, { type: 'STOP' });
      workerRef.current.terminate();
      workerRef.current = null;
    }
  }, []);

  /**
   * Push the current access token into the worker.
   *
   * The worker holds its own copy and cannot read the cookie, so every rotation has to
   * be handed to it explicitly. Miss one and it 401s on every tick while the rest of the
   * app carries on with the fresh token — silently dropping the user offline once the
   * server-side presence TTL lapses.
   */
  const syncWorkerAuth = useCallback(() => {
    if (!workerRef.current) return;
    const { token, orgId } = getAuthContext();
    postToWorker(workerRef.current, { type: 'UPDATE_AUTH', token, orgId });
  }, []);

  const ensureWorker = useCallback((): Worker => {
    if (workerRef.current) return workerRef.current;

    const worker = new Worker(new URL('../workers/heartbeat.worker.ts', import.meta.url), {
      type: 'module',
    });

    worker.onmessage = (event: MessageEvent<HeartbeatWorkerOutMessage>) => {
      const msg = event.data;
      switch (msg.type) {
        case 'TOKEN_EXPIRED':
          // Usual case: axios already rotated the cookie token and the worker is simply
          // behind. Hand it straight over so the next tick succeeds.
          syncWorkerAuth();
          // Fallback: if the cookie token is stale too, this refetch 401s, the axios
          // interceptor refreshes, and subscribeToTokenRefresh syncs the worker.
          queryClient.invalidateQueries({ queryKey: AVAILABILITY_QUERY_KEY });
          break;
        case 'HEARTBEAT_ERROR':
          queryClient.invalidateQueries({ queryKey: AVAILABILITY_QUERY_KEY });
          break;
        case 'HEARTBEAT_OK':
          // Heartbeat succeeded — nothing to do.
          break;
      }
    };

    workerRef.current = worker;
    return worker;
  }, [queryClient, syncWorkerAuth]);

  const startHeartbeat = useCallback(
    (intervalSeconds: number) => {
      const worker = ensureWorker();
      const { token, orgId } = getAuthContext();
      postToWorker(worker, {
        type: 'START',
        url: HEARTBEAT_URL,
        token,
        orgId,
        intervalMs: intervalSeconds * 1000,
      });
    },
    [ensureWorker],
  );

  const stopHeartbeat = useCallback(() => {
    terminateWorker();
  }, [terminateWorker]);

  const setAvailability = useMutation({
    mutationFn: (newAvailable: boolean) =>
      userService.setMyAvailability({ available: newAvailable }),
    onSuccess: response => {
      queryClient.setQueryData(AVAILABILITY_QUERY_KEY, { available: response.available });
      storeAvailability(response.available ?? false);
      if (!mountedRef.current) return;
      if (response.available && response.heartbeat_interval_seconds) {
        startHeartbeat(response.heartbeat_interval_seconds);
      } else {
        stopHeartbeat();
      }
    },
    onError: () => {
      toast.error('Failed to update availability');
    },
  });

  // Detect login/logout transitions
  useEffect(() => {
    const wasAuthenticated = prevAuthRef.current;
    prevAuthRef.current = isAuthenticated;

    if (isAuthenticated && !wasAuthenticated) {
      // Login — always mark available (ignoring any stored preference)
      userService
        .setMyAvailability({ available: true })
        .then(resp => {
          if (!mountedRef.current) return;
          queryClient.setQueryData(AVAILABILITY_QUERY_KEY, { available: resp.available });
          storeAvailability(resp.available ?? false);
          if (resp.available && resp.heartbeat_interval_seconds) {
            startHeartbeat(resp.heartbeat_interval_seconds);
          }
        })
        .catch(() => {
          // Best-effort — availability will default to unavailable
        });
    }

    if (!isAuthenticated && wasAuthenticated) {
      // Logout — stop heartbeat and clear cached availability
      stopHeartbeat();
      queryClient.removeQueries({ queryKey: AVAILABILITY_QUERY_KEY });
      clearStoredAvailability();
    }
  }, [isAuthenticated, startHeartbeat, stopHeartbeat, queryClient]);

  // Restore availability from localStorage on page refresh. If the user was
  // previously available but the server marked them unavailable (heartbeats
  // expired while the tab was closed), this re-establishes their preference.
  // Skipped on fresh login — the login transition effect always sets available.
  useEffect(() => {
    if (!wasAuthenticatedOnMountRef.current || !isAuthenticated) return;
    if (available) return; // server already says available, page-refresh effect handles this
    if (getStoredAvailability() !== true) return;

    userService
      .setMyAvailability({ available: true })
      .then(resp => {
        if (!mountedRef.current) return;
        queryClient.setQueryData(AVAILABILITY_QUERY_KEY, { available: resp.available });
        if (resp.available && resp.heartbeat_interval_seconds) {
          startHeartbeat(resp.heartbeat_interval_seconds);
        }
      })
      .catch(() => {
        // Best-effort — user can manually toggle availability
      });
  }, [isAuthenticated, available, queryClient, startHeartbeat]);

  // When already authenticated on mount (page refresh) and available,
  // re-set to get heartbeat_interval_seconds from server.
  useEffect(() => {
    if (!isAuthenticated || !available || workerRef.current) return;

    userService
      .setMyAvailability({ available: true })
      .then(resp => {
        if (mountedRef.current && resp.available && resp.heartbeat_interval_seconds) {
          startHeartbeat(resp.heartbeat_interval_seconds);
        }
      })
      .catch(() => {
        // Best-effort
      });

    return () => {
      stopHeartbeat();
    };
  }, [available, isAuthenticated, startHeartbeat, stopHeartbeat]);

  // When the browser tab becomes visible again, refresh the auth context
  // sent to the worker. The token may have been refreshed while the tab
  // was hidden, and the worker needs the latest credentials.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!isAuthenticated || !available) return;

      // Update the worker's auth context with potentially refreshed token.
      syncWorkerAuth();

      // Also re-confirm availability with the server — this handles edge
      // cases where the worker's heartbeats couldn't reach the server and
      // the server marked the user as unavailable.
      userService
        .setMyAvailability({ available: true })
        .then(resp => {
          if (!mountedRef.current) return;
          if (resp.available && resp.heartbeat_interval_seconds) {
            startHeartbeat(resp.heartbeat_interval_seconds);
          }
        })
        .catch(() => {
          // Best-effort — next heartbeat tick will reconcile
        });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, available, startHeartbeat, syncWorkerAuth]);

  // A token refresh can land at any time (any 401 wakes the axios interceptor), not just
  // while the tab is hidden — so the worker has to be told then too, not only on the
  // visibility change above.
  useEffect(() => subscribeToTokenRefresh(syncWorkerAuth), [syncWorkerAuth]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopHeartbeat();
    };
  }, [stopHeartbeat]);

  const setAvailable = useCallback(
    (value: boolean) => setAvailability.mutate(value),
    [setAvailability],
  );

  const value = useMemo<AvailabilityContextValue>(
    () => ({
      available,
      isLoading: isAuthenticated ? isLoading : false,
      isPending: setAvailability.isPending,
      setAvailable,
    }),
    [available, isAuthenticated, isLoading, setAvailability.isPending, setAvailable],
  );

  return <AvailabilityContext.Provider value={value}>{children}</AvailabilityContext.Provider>;
};

/**
 * Hook to consume the availability context.
 * Must be used within an AvailabilityProvider.
 */
export const useAvailabilityContext = (): AvailabilityContextValue => {
  const ctx = useContext(AvailabilityContext);
  if (!ctx) {
    throw new Error('useAvailabilityContext must be used within an AvailabilityProvider');
  }
  return ctx;
};
